import { Customer } from './customer.service'
import {Location, LocationService} from './location.service'
import { Entity } from './entity.service'
import { Status } from './status.service'
import { Spot } from './group.service'
import { ApiError, ApiService } from './api.service'
import { Constants } from '../constants'
import moment from 'moment'
import { TimezoneService } from "../services/timezone.service";

/**
 * Dropdown class for bookings
 */
class BookingDropdown {
    constructor(id, licensePlate, customer, options, startdate, arrivalDate, departureDate) {
        this.id = id
        this.licensePlate = licensePlate
        this.customer = customer
        this.options = options // all booking options + values
        this.startdate = startdate
        switch (true) {
            case arrivalDate != undefined && departureDate == undefined:
                this.value = "Arrived"
                break
            case arrivalDate != undefined && departureDate != undefined:
                this.value = "Departed"
                break
            default:
                this.value = "Expected"
                break
        }

    }
}

class Booking {
    constructor(booking) {
        this.id = booking ? booking.id : undefined
        this.isRecurringBooking = booking ? booking.isRecurringBooking : false
        this.vehicleArrivalDate = booking ? booking.vehicleArrivalDate : undefined
        this.vehicleDepartureDate = booking ? booking.vehicleDepartureDate : undefined
        this.recurringEndDate = booking ? booking.recurringEndDate || new Date() : new Date()
        this.recurringIntervalWeeks = booking ? booking.recurringIntervalWeeks : undefined
        this.licensePlate = booking ? booking.licensePlate : undefined
        this.trailerPlate = booking ? booking.trailerPlate : undefined
        this.notifyOnArrival = booking ? booking.notifyOnArrival == 1 : false
        this.customer = booking ? booking.customer || new Customer() : new Customer()
        this.location = booking ? booking.location || new Location() : new Location()
        this.entity = booking ? booking.entity || new Entity() : new Entity()
        this.options = booking ? booking.options || [] : []
        this.status = booking ? booking.status || new Status() : new Status()
        this.spots = booking && booking.spots ? booking.spots.map(o => new Spot(o)) || [] : []
        this.emailAddresses = booking ? booking.emailAddresses : []
        this.userCreated = booking ? booking.userCreated : undefined
        // Extensive properties
        this.arrivalDate = new Date()
        this.timezone = this?.location?.timezoneName ?? undefined
    }

    mount(params, query) {
        this.id = params.id
        this.location.id = query.locationId

        this.entity.id = query.entityId
        this.entity.startDate = query.startDate ? new Date(query.startDate).toISOString() : undefined
        this.entity.endDate = query.endDate ? new Date(query.endDate).toISOString() : undefined
        if (query.spotId != undefined) {
            let spot = new Spot()
            spot.id = query.spotId
            spot.label = query.label
            spot.selected = true

            this.spots.push(spot)
        }
        this.arrivalDate = query.startDate ? new Date(query.startDate) : new Date()
    }

    isRequested() {
        return (this.status && this.status.id == 1) || this.id == undefined
    }

    isAccepted() {
        return this.status && this.status.id == 2
    }

    isDeclined() {
        return this.status && this.status.id == 3
    }

    getSelectedGroup() {
        for (const spot of this.spots) {
            for (const group of this.location.groups) {
                const selectedSpot = group.spots.filter(o => o.id == spot.id)
                if (selectedSpot.length > 0) {
                    return group
                }
            }
        }
        return this.location.groups[0]
    }

}

class BookingOptionTypeDropDown {
    constructor(id, type) {
        this.id = id
        this.value = type
    }
}

class BookingOptionDropDown {
    constructor(id, name, checked=false) {
        this.id = id
        this.value = name
        this.checked = checked
    }
}

const BookingOptionType = {
    Text: "Text",
    Numbers: "Numbers",
    Boolean: "Boolean",
    Date : "Date",
    Select: "Select"
}

class BookingOption {
    constructor(bookingOption) {
        this.id = bookingOption ? bookingOption.id : undefined
        this.value = bookingOption ? bookingOption.value : undefined
        this.name = bookingOption ? bookingOption.name : undefined
        this.type = bookingOption ? bookingOption.type : undefined
        this.usedForVerification = bookingOption ? bookingOption.usedForVerification == 1 : false
        this.usedForVisitsDisplay = bookingOption ? bookingOption.usedForVisitsDisplay == 1 : false
        this.usedForArrivalsDisplay = bookingOption ? bookingOption.usedForArrivalsDisplay == 1 : false
        this.usedForSMS = bookingOption ? bookingOption.usedForSMS == 1 : false
        this.usedForGosReference = bookingOption ? bookingOption.usedForGosReference == 1 : false
        this.mandatory = bookingOption ? bookingOption.mandatory == 1 : false
        this.isGenerated = bookingOption ? bookingOption.isGenerated == 1 : false;
        this.isForSuperUser = bookingOption ? bookingOption.isForSuperUser == 1 : false;
        this.pattern = bookingOption ? bookingOption.pattern : undefined
        this.options = this.parseOptions(bookingOption)
        this.timeSlotLengthFormulas = bookingOption ? bookingOption.timeSlotLengthFormulas : undefined
        this.locationId = bookingOption ? bookingOption.locationId : undefined
        this.original = true
    }

    parseOptions (bookingOption) {
        if (bookingOption?.options) {
            try {
                return  JSON.parse(bookingOption.options)
            } catch (error) {
                return undefined
            }
        }
        return undefined
    }

    formulaMatchesValue(formula) {
        let value = 0
        if (this.type == BookingOptionType.Boolean) {
            //Convert boolean
            value = this.value ? 1 : 0
        } else if (this.type == BookingOptionType.Numbers) {
            //Convert number
            value = parseInt(this.value)
            if (isNaN(value)) {
                return false
            }
        } else {
            //We can't calculate with Text bookingOptions. Always match
            return true
        }
        if (formula.from && value < formula.from) {
            return false
        }
        if (formula.to && value > formula.to) {
            return false
        }
        return true
    }

    static calculateRequiredMinimalTimeSlotLength(bookingOptions) {
        let result = 0
        try {
            for (let option of bookingOptions) {
                if (option.timeSlotLengthFormulas == undefined || !Array.isArray(option.timeSlotLengthFormulas)) {
                    continue
                }
                for (let formula of option.timeSlotLengthFormulas) {
                    if (formula.addMinutes) {
                        if (option.formulaMatchesValue(formula)) {
                            result += formula.addMinutes
                        }
                    }
                }
            }
        }
        catch(error) {
            console.warn("Failed calculating required minimal time slot length")
            console.warn(error)
        }
        return result
    }
}

const BookingService = {

    createBooking: async function(booking) {
        try {
            let correctedBooking = TimezoneService.correctTimezoneDifferenceUp(booking) // correct booking timezone
            correctedBooking.timezone = correctedBooking.location.timezoneName // add the timezone name espacially for recurring visits
            const response = await ApiService.post(Constants.ENDPOINT_BOOKINGS, correctedBooking)
            return response
        }catch(error) {
            throw new ApiError(error)
        }
    },

    updateBooking: async function(booking, userMessage = undefined, deleteSeries = false) {
        try {
            let correctedBooking = TimezoneService.correctTimezoneDifferenceUp(booking) // correct booking timezone
            let endpoint = `${Constants.ENDPOINT_BOOKINGS}/${correctedBooking.id}`
            if (deleteSeries) endpoint = endpoint + '?deleteSeries=true'
            const response = await ApiService.put(endpoint, {booking: correctedBooking, userMessage: userMessage})
            return response
        }catch(error) {
            throw new ApiError(error)
        }
    },

    getBooking: async function(bookingId) {
        try {
            const endpoint = `${Constants.ENDPOINT_BOOKINGS}/${bookingId}`
            const response = await ApiService.get(endpoint)
            let booking = response.data
            // correct the timezone
            let location = await LocationService.getLocation(booking.location.id) // get the location (used for retrieving the timezone offset)
            booking = TimezoneService.correctTimezoneDifferenceDown(booking, location) // correct the booking
            return booking
        }catch(error) {
            throw new ApiError(error)
        }
    },

    getBookingsByLocation: async function(location, startDate, endDate, returnRejectedBookings) {
        let queryString = "/" + location.id
        if (startDate !== undefined) {
            queryString += "?startDate=" + encodeURIComponent(startDate)
        }
        if (endDate !== undefined) {
            if (endDate === undefined) {
                queryString += "?endDate=" + encodeURIComponent(endDate)
            } else {
                queryString += "&endDate=" + encodeURIComponent(endDate)
            }
        }

        let url = `${Constants.ENDPOINT_BOOKINGS_BY_LOCATION}${queryString}`
        let bookings = []
        bookings = await ApiService.get(url)

        return returnRejectedBookings ? bookings.data : bookings.data.filter(b => b.status.id !== '3')
    },

    getBookingOptions: async function(locationId) {
        try {
            const endpoint = Constants.ENDPOINT_BOOKING_OPTIONS
            const response = await ApiService.get(`${endpoint}/${locationId}`)
            return response.data
        } catch(error) {
            throw new ApiError(error)
        }
    },

    getBookingOptionById: async function(id) {
        try {
            const endpoint = Constants.ENDPOINT_BOOKING_OPTION+ "/" + id
            const response = await ApiService.get(endpoint)
            return response.data
        } catch(error) {
            throw new ApiError(error)
        }
    },
    createBookingOption: async function(option) {
        try {
            if (typeof option.pattern != "string") {
                option.pattern = ""
            }
            const response = await ApiService.post(Constants.ENDPOINT_BOOKING_OPTIONS, option)
            return response
        }catch(error) {
            throw new ApiError(error)
        }

    },
    editBookingOption: async function(option) {
        try {
            if (typeof option.pattern != "string") {
                option.pattern = ""
            }
            const endpoint = `${Constants.ENDPOINT_BOOKING_OPTION}/${option.id}`
            const response = await ApiService.put(endpoint, option)
            return response
        }catch(error) {
            throw new ApiError(error)
        }
    },

    checkSpotOverlap: async function(groupId, startDate, endDate) {
        try {
            const endpoint = `${Constants.ENDPOINT_BOOKINGS}/checkOverlap/${groupId}?startDate=${encodeURIComponent(startDate)}&endDate=${encodeURIComponent(endDate)}`
            const response = await ApiService.get(endpoint)
            return response
        } catch (error) {
            throw new ApiError(error)
        }
    }

}

export { BookingService, Booking, BookingDropdown, BookingOptionType, BookingOption, BookingOptionTypeDropDown,BookingOptionDropDown }
